import React, {
  useRef,
  useState,
  useEffect,
} from "react";

import SignatureCanvas
from "react-signature-canvas";

import jsPDF from "jspdf";

import {
  FileText,
  MapPin,
  PenTool,
  Upload,
  CheckCircle2,
  Eraser,
  ShieldCheck,
} from "lucide-react";

import "./BrokerAppointmentForm.css";

const BrokerAppointmentForm = ({
  fullName = "",
  address = "",
  onFinish,
}) => {

  const [clientName,
    setClientName] =
      useState(fullName);

  const [clientAddress,
    setClientAddress] =
      useState(address);

  const [isTopazAvailable,
    setIsTopazAvailable] =
      useState(false);

  const [signatureData,
    setSignatureData] =
      useState("");

  const [signatureSaved,
    setSignatureSaved] =
      useState(false);

  const topazCanvasRef =
    useRef(null);

  const sigCanvasRef =
    useRef(null);

  const [sigObj,
    setSigObj] =
      useState(null);

  useEffect(() => {

    if (window.Topaz) {
      setIsTopazAvailable(true);
    }

  }, []);

  const startTopazCapture =
    () => {

    if (window.Topaz) {

      const sig =
        new window.Topaz.UserControl();

      sig.JustifyMode = 0;

      sig.SetTabletState(
        1,
        topazCanvasRef.current.id
      );

      setSigObj(sig);
    }
  };

  const clearSignature =
    () => {

    setSignatureSaved(false);

    setSignatureData("");

    if (
      isTopazAvailable &&
      sigObj
    ) {

      sigObj.ClearTablet();

    } else {

      sigCanvasRef.current.clear();
    }
  };

  const saveSignature =
    () => {

    try {

      if (isTopazAvailable) {

        if (sigObj) {

          const base64Image =
            sigObj.GetSigImageB64();

          setSignatureData(
            base64Image
          );

          setSignatureSaved(true);
        }

} else {

  if (
    !sigCanvasRef.current ||
    sigCanvasRef.current.isEmpty()
  ) {
    return;
  }

  // GET RAW CANVAS
  const rawCanvas =
    sigCanvasRef.current.getCanvas();

  // CREATE TEMP CANVAS
  const tempCanvas =
    document.createElement("canvas");

  tempCanvas.width =
    rawCanvas.width;

  tempCanvas.height =
    rawCanvas.height;

  const ctx =
    tempCanvas.getContext("2d");

  // WHITE BACKGROUND
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(
    0,
    0,
    tempCanvas.width,
    tempCanvas.height
  );

  // DRAW SIGNATURE
  ctx.drawImage(
    rawCanvas,
    0,
    0
  );

  const base64Image =
    tempCanvas
      .toDataURL("image/png")
      .split(",")[1];

  setSignatureData(
    base64Image
  );

  setSignatureSaved(true);
}

    } catch (err) {

      console.error(
        "Signature Save Error:",
        err
      );
    }
  };

  const handleFinish =
    async () => {

    if (!signatureData)
      return;

    const doc =
      new jsPDF();

    const marginLeft = 20;

    let cursor = 20;

    doc.setFontSize(12);

    doc.text(
      clientName,
      marginLeft,
      cursor
    );

    cursor += 7;

    doc.text(
      clientAddress,
      marginLeft,
      cursor
    );

    cursor += 12;

    doc.text(
      "The Director/Secretary,",
      marginLeft,
      cursor
    );

    cursor += 7;

    doc.text(
      "Apex Insurance Brokers Inc.,",
      marginLeft,
      cursor
    );

    cursor += 7;

    doc.text(
      "125 C Barrack Street,",
      marginLeft,
      cursor
    );

    cursor += 7;

    doc.text(
      "Kingston Georgetown.",
      marginLeft,
      cursor
    );

    cursor += 14;

    doc.text(
      "Dear Sir,",
      marginLeft,
      cursor
    );

    cursor += 10;

    const body = [

"I hereby appoint APEX INSURANCE BROKERS INC. as my Broker in respect to all my insurance",

"matters and further authorise Apex to deal with any insurance company on my behalf.",

"Such company is obliged to recognise Apex as my broker.",

"",

"This appointment does not preclude me from any decision regarding the placement of",

"my insurance business.",

"",

"This letter supersedes any existing arrangement and/or contract with any other",

"Broker or with any Agent."

    ];

    body.forEach((line) => {

      doc.text(
        line,
        marginLeft,
        cursor
      );

      cursor += 7;
    });

    cursor += 10;

    doc.text(
      "Yours Sincerely,",
      marginLeft,
      cursor
    );

    cursor += 10;

    doc.text(
      clientName,
      marginLeft,
      cursor
    );

    cursor += 15;

    doc.addImage(
`data:image/png;base64,${signatureData}`,
      "PNG",
      marginLeft,
      cursor,
      60,
      25
    );

    const blob =
      doc.output("blob");

    const file =
      new File(
        [blob],
`broker_appointment_${Date.now()}.pdf`,
{
  type:
    "application/pdf",
}
);

    onFinish(file);
  };

  return (

<div className="modern-broker-container">

  {/* HEADER */}

  <div className="modern-broker-header">

    <div className="modern-broker-icon">
      <ShieldCheck size={30} />
    </div>

    <div>

      <h3>
        Broker Appointment Form
      </h3>

      <p>
        Complete and sign your
        broker authorization form.
      </p>

    </div>

  </div>

  {/* INPUTS */}

  <div className="modern-broker-grid">

    <div className="modern-broker-group">

      <label>
        Full Name
      </label>

      <div className="modern-broker-input">

        <FileText size={18} />

        <input
          type="text"
          placeholder="Enter full name"
          value={clientName}
          onChange={(e) =>
            setClientName(
              e.target.value
            )
          }
        />

      </div>

    </div>

    <div className="modern-broker-group">

      <label>
        Address
      </label>

      <div className="modern-broker-input">

        <MapPin size={18} />

        <input
          type="text"
          placeholder="Enter address"
          value={clientAddress}
          onChange={(e) =>
            setClientAddress(
              e.target.value
            )
          }
        />

      </div>

    </div>

  </div>

  {/* SIGNATURE */}

  <div className="modern-signature-box">

    <div className="modern-signature-header">

      <div>

        <h4>
          Signature
        </h4>

        <span>
          {isTopazAvailable
            ? "Topaz Device"
            : "Touch / Mouse"}
        </span>

      </div>

      {signatureSaved && (

        <div className="modern-signature-saved">

          <CheckCircle2
            size={16}
          />

          Signature Saved

        </div>
      )}

    </div>

    {isTopazAvailable ? (

      <canvas
        ref={topazCanvasRef}
        id="signatureCanvas"
        className="modern-topaz-canvas"
      />

    ) : (

      <div className="modern-signature-pad-wrap">

        <SignatureCanvas
          ref={sigCanvasRef}
          penColor="black"

          canvasProps={{
            className:
              "modern-signature-pad",
          }}
        />

      </div>

    )}

  </div>

  {/* BUTTONS */}

  <div className="modern-signature-actions">

    {isTopazAvailable && (

      <button
        type="button"
        className="modern-broker-btn dark"
        onClick={startTopazCapture}
      >

        <PenTool size={18} />

        Start Capture

      </button>

    )}

    <button
      type="button"
      className="modern-broker-btn"
      onClick={clearSignature}
    >

      <Eraser size={18} />

      Clear

    </button>

    <button
      type="button"
      className="modern-broker-btn"
      onClick={saveSignature}
    >

      <CheckCircle2 size={18} />

      Save Signature

    </button>

    <button
      type="button"
      className="modern-broker-btn gold"
      onClick={handleFinish}
    >

      <Upload size={18} />

      Finish & Upload

    </button>

  </div>

  {/* PREVIEW */}

  {signatureData && (

    <div className="modern-signature-preview">

      <h4>
        Signature Preview
      </h4>

      <img
src={`data:image/png;base64,${signatureData}`}
        alt="Signature Preview"
      />

    </div>
  )}

</div>
  );
};

export default BrokerAppointmentForm;